import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	listenForForegroundMessages,
	refreshPushToken,
} from '../../services/pushNotifications';

/**
 * In-app notice for FOREGROUND push messages (app open + focused).
 *
 * Task pushes are DATA-ONLY (see functions/sendPushToUid), so FCM never
 * auto-displays them, and the service worker's onBackgroundMessage only fires
 * when the tab is NOT focused. That left a gap: a push arriving while the portal
 * is open and focused showed nothing at all. This wires the previously-unused
 * listenForForegroundMessages() to a lightweight dismissible banner; tapping it
 * follows the same deep-link the background notificationclick uses.
 *
 * Mutually exclusive with the SW background path (foreground vs backgrounded tab),
 * so it can't double-fire with the OS notification. Self-managed visibility —
 * renders nothing until a message arrives. Mount once high in the staff shell.
 */
function ForegroundPushListener() {
	const navigate = useNavigate();
	const [notice, setNotice] = useState(null); // { title, body, link } | null

	useEffect(() => {
		let unsub = null;
		let alive = true;
		// Fire-and-forget: revalidate this device's push token every session, so
		// a rotated subscription can't silently strand pushes (see the function's
		// header comment for the 2026-08-14 zombie-token incident).
		refreshPushToken();
		(async () => {
			const off = await listenForForegroundMessages((payload) => {
				const d = (payload && payload.data) || {};
				setNotice({
					title: d.title || 'Color Your Voice',
					body: d.body || '',
					link: d.link || '/portal/work',
				});
			});
			if (!alive) {
				// Unmounted before the async subscribe resolved — tear it down now.
				if (typeof off === 'function') off();
				return;
			}
			unsub = off;
		})();
		return () => {
			alive = false;
			if (typeof unsub === 'function') unsub();
		};
	}, []);

	// Auto-dismiss so a stale banner doesn't linger.
	useEffect(() => {
		if (!notice) return undefined;
		const t = setTimeout(() => setNotice(null), 8000);
		return () => clearTimeout(t);
	}, [notice]);

	if (!notice) return null;

	const open = () => {
		const { link } = notice;
		setNotice(null);
		navigate(link);
	};

	return (
		<div
			role='alert'
			style={{
				position: 'fixed',
				left: '50%',
				bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
				transform: 'translateX(-50%)',
				zIndex: 2000,
				width: 'min(92vw, 420px)',
				display: 'flex',
				alignItems: 'flex-start',
				gap: '0.75rem',
				padding: '0.85rem 1rem',
				borderRadius: '12px',
				background: 'var(--wa-color-surface-raised, #1f2430)',
				color: 'var(--wa-color-text-normal, #f4f4f5)',
				boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
				border: '1px solid var(--wa-color-surface-border, rgba(255,255,255,0.08))',
			}}>
			<button
				type='button'
				onClick={open}
				style={{
					flex: 1,
					display: 'flex',
					alignItems: 'flex-start',
					gap: '0.65rem',
					background: 'transparent',
					border: 'none',
					padding: 0,
					margin: 0,
					color: 'inherit',
					textAlign: 'left',
					cursor: 'pointer',
					font: 'inherit',
				}}>
				<i
					className='fa-sharp fa-solid fa-bell'
					style={{ marginTop: '0.15rem', opacity: 0.9 }}
					aria-hidden='true'
				/>
				<span style={{ minWidth: 0 }}>
					<span style={{ display: 'block', fontWeight: 600 }}>
						{notice.title}
					</span>
					{notice.body && (
						<span style={{ display: 'block', opacity: 0.85, fontSize: '0.9em' }}>
							{notice.body}
						</span>
					)}
				</span>
			</button>
			<button
				type='button'
				aria-label='Dismiss notification'
				onClick={() => setNotice(null)}
				style={{
					background: 'transparent',
					border: 'none',
					color: 'inherit',
					opacity: 0.7,
					cursor: 'pointer',
					padding: '0.15rem',
					lineHeight: 1,
				}}>
				<i className='fa-sharp fa-solid fa-xmark' aria-hidden='true' />
			</button>
		</div>
	);
}

export default ForegroundPushListener;
